import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getCompanyInfo } from "../../../../shared/CommonFunctions";

const loadLogoImage = (logoPath) =>
  new Promise((resolve) => {
    if (!logoPath) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = logoPath.startsWith("http") ? logoPath : `/assets/img/${logoPath}`;
  });

const formatValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

export async function createReportPdfDoc({ title, orientation = "portrait" }) {
  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 10;
  let y = 12;

  const companyInfo = await getCompanyInfo();
  const companyName = companyInfo?.CompanyName || "Company";
  const companyAddress = companyInfo?.Address || "";
  const companyEmail = companyInfo?.EmailId || "";
  const logoPath = companyInfo?.LogoImagePath || "";
  const logoImage = await loadLogoImage(logoPath);

  if (logoImage) {
    const logoWidth = 40;
    const logoHeight = 24;
    doc.addImage(logoImage, "PNG", marginX, y - 10, logoWidth, logoHeight);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(companyName, pageWidth / 2, y, { align: "center" });
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  if (companyAddress) {
    doc.text(companyAddress, pageWidth / 2, y, { align: "center" });
    y += 4;
  }
  if (companyEmail) {
    doc.text(companyEmail, pageWidth / 2, y, { align: "center" });
    y += 4;
  }

  y += 2;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(title || "Report", marginX, y + 6);

  return { doc, pageWidth, marginX, y: y + 10 };
}

export function drawFilterAndKpiBoxes({ doc, pageWidth, marginX, startY, filters = {}, kpis = [] }) {
  let y = startY;
  const boxWidth = pageWidth - marginX * 2;

  const filterLines = Object.entries(filters || {}).map(([k, v]) => `${k}: ${formatValue(v)}`);
  const filterText = filterLines.length ? filterLines.join("   |   ") : "Filters: -";

  // Filters box
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.rect(marginX, y, boxWidth, 14);
  doc.text("FILTERS:", marginX + 3, y + 6);
  doc.setTextColor(0, 102, 204);
  const split = doc.splitTextToSize(filterText, boxWidth - 20);
  doc.text(split, marginX + 20, y + 6);
  doc.setTextColor(0, 0, 0);
  y += 18;

  // KPI box
  if (Array.isArray(kpis) && kpis.length) {
    const kpiBoxHeight = 14;
    doc.rect(marginX, y, boxWidth, kpiBoxHeight);
    doc.text("SUMMARY:", marginX + 3, y + 6);
    let x = marginX + 20;
    kpis.slice(0, 6).forEach((k) => {
      const label = String(k?.label || "").trim();
      const value = formatValue(k?.value);
      if (!label) return;
      doc.setTextColor(0, 0, 0);
      doc.text(`${label}:`, x, y + 6);
      x += doc.getTextWidth(`${label}:`) + 2;
      doc.setTextColor(0, 102, 204);
      doc.text(value || "-", x, y + 6);
      doc.setTextColor(0, 0, 0);
      x += doc.getTextWidth(value || "-") + 8;
    });
    y += 18;
  }

  return y;
}

export function drawTable({ doc, startY, columns = [], rows = [] }) {
  const head = [columns.map((c) => String(c?.name ?? ""))];
  const body = (rows || []).map((r) =>
    (columns || []).map((c) => {
      try {
        const v = typeof c.selector === "function" ? c.selector(r) : r?.[c.selector];
        return v === null || v === undefined ? "" : String(v);
      } catch {
        return "";
      }
    })
  );

  autoTable(doc, {
    head,
    body,
    startY,
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [240, 240, 240], textColor: 0, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    margin: { left: 10, right: 10 },
  });
}

